/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import React from "react";
import { Sparkline } from "@/components/common/Sparkline";
import { cn } from "@/lib/utils";
import type { SpeedTestResult } from "@/types/types";
import { mean, pingOf, type ServerStats } from "./speedStats";

const fmt = (n: number) => (n >= 1000 ? `${(n / 1000).toFixed(2)} Gbps` : `${n.toFixed(0)} Mbps`);

function ago(iso: string): string {
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  return h < 48 ? `${h}h ago` : `${Math.round(h / 24)}d ago`;
}

/** Every measured server side by side: one row each, the selected one drives the charts. */
export const SpeedByServer: React.FC<{
  servers: ServerStats[];
  selected: string;
  onSelect: (name: string) => void;
}> = ({ servers, selected, onSelect }) => {
  const top = Math.max(1, ...servers.map((s) => s.bestDown));
  return (
    <div className="overflow-x-auto">
      <div className="min-w-[620px] text-[12.5px]" role="table" aria-label="Speed by server">
        <div className="grid grid-cols-[minmax(110px,1.2fr)_minmax(185px,1.5fr)_84px_62px_84px_46px] items-center gap-3 border-b border-gray-200 px-2 pb-1.5 text-[10.5px] uppercase tracking-wide text-gray-400 dark:border-white/10" role="row">
          <span>Server</span>
          <span>Download</span>
          <span>Upload</span>
          <span>Ping</span>
          <span>Last test</span>
          <span className="text-right">Tests</span>
        </div>
        {servers.map((s) => {
          const on = selected === s.name;
          return (
            <button
              key={s.name}
              type="button"
              role="row"
              aria-pressed={on}
              onClick={() => onSelect(s.name)}
              className={cn(
                "grid w-full grid-cols-[minmax(110px,1.2fr)_minmax(185px,1.5fr)_84px_62px_84px_46px] items-center gap-3 border-b border-gray-100 px-2 py-2 text-left transition-colors last:border-0 dark:border-white/5",
                on ? "bg-emerald-500/10" : "hover:bg-gray-50 dark:hover:bg-white/[0.04]",
              )}
            >
              <span className="flex min-w-0 items-center gap-2">
                <span className={cn("h-1.5 w-1.5 flex-none rounded-full", on ? "bg-emerald-500" : "bg-gray-300 dark:bg-white/25")} />
                <span className="truncate font-medium text-gray-900 dark:text-gray-100">{s.name}</span>
              </span>
              <span className="min-w-0">
                <span className="flex items-baseline justify-between gap-2 tabular-nums">
                  <b className="whitespace-nowrap font-semibold text-gray-900 dark:text-white">{fmt(s.latest.downloadSpeed)}</b>
                  <span className="whitespace-nowrap text-[11px] text-gray-400">{`avg ${s.avgDown.toFixed(0)} · best ${s.bestDown.toFixed(0)}`}</span>
                </span>
                <span className="mt-1 block h-1 overflow-hidden rounded-full bg-gray-200 dark:bg-white/10">
                  <span className="block h-full rounded-full bg-emerald-500" style={{ width: `${Math.min(100, (s.latest.downloadSpeed / top) * 100)}%` }} />
                </span>
              </span>
              <span className="tabular-nums text-gray-700 dark:text-gray-300">{s.latest.uploadSpeed >= 1 ? fmt(s.latest.uploadSpeed) : "—"}</span>
              <span className="tabular-nums text-gray-700 dark:text-gray-300">{`${pingOf(s.latest).toFixed(0)} ms`}</span>
              <span className="text-gray-500 dark:text-gray-400">{ago(s.latest.createdAt)}</span>
              <span className="text-right tabular-nums text-gray-500 dark:text-gray-400">{s.rows.length}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

/** Download / upload / ping history of the chosen rows (oldest -> newest). */
export const SpeedCharts: React.FC<{ rows: SpeedTestResult[] }> = ({ rows }) => {
  if (rows.length < 2) {
    return <div className="flex h-24 items-center justify-center text-sm text-gray-500 dark:text-gray-400">Not enough data from this server yet</div>;
  }
  return (
    <div className="space-y-4">
      {(
        [
          { label: "Download", get: (r: SpeedTestResult) => r.downloadSpeed, color: "#5fa37a", unit: "Mbps" },
          { label: "Upload", get: (r: SpeedTestResult) => r.uploadSpeed, color: "#c98a5b", unit: "Mbps" },
          { label: "Ping", get: pingOf, color: "#e0a63a", unit: "ms" },
        ] as const
      )
        .filter((s) => s.label !== "Upload" || rows.some((r) => r.uploadSpeed >= 1)) // mirrors are download-only
        .map((s) => {
        const vals = rows.map(s.get);
        return (
          <div key={s.label}>
            <div className="mb-1 flex items-baseline justify-between text-[12px] text-gray-500 dark:text-gray-400">
              <span className="font-medium text-gray-700 dark:text-gray-300">{s.label}</span>
              <span className="tabular-nums">{`min ${Math.min(...vals).toFixed(0)} · avg ${mean(vals).toFixed(0)} · max ${Math.max(...vals).toFixed(0)} ${s.unit}`}</span>
            </div>
            <Sparkline values={vals} color={s.color} height={52} />
          </div>
        );
      })}
    </div>
  );
};
